import {
    DisplayFeatureType, NvdbDeletion, NvdbFeature,
    UserSession, AssociationScope, CommonAssociationFeature, DisplayFeature
} from '../../contract-features-3/domain';
import {AssociatedFeature} from "../domain/AssociatedFeature";
import {AssociationJson} from "../domain/AssociationJson";
import {FeatureTypeSummary, SkinnyFeatureType} from "../../../../integration/representation/feature-type";
import {Definition} from "../domain/Definition";
import datacatalog, {AttributeType} from "../../../../integration/datacatalog/datacatalog-services";

export type splitDirection = 'horizontal' | 'vertical';

export interface FeatureTypeAttributes extends SkinnyFeatureType{
    attributes: { [key: string]: AttributeType };
}

export default class AssociationsState {
    constructor(
        readonly datacatalog: datacatalog = null,
        readonly contractId: string = null,
        readonly isFetching: boolean = false,
        readonly featureTypes: FeatureTypeSummary[] = [],
        readonly selectedFeatureTypeId: number = null,
        readonly selectedFeatureType: DisplayFeatureType = null,
        readonly additionalSelectedFeatureTypes: DisplayFeatureType[] = [],
        readonly features: DisplayFeature[] = [],
        readonly selectedFeatures: string[] = [],
        readonly user: UserSession = null,
        readonly isShowingExistingFeatures: boolean = false,
        readonly existingFeatures: NvdbFeature[] = [],
        readonly deletions: NvdbDeletion[] = [],
        readonly showingFullscreen: boolean = false,
        readonly splitScreenDirection: splitDirection = 'horizontal',
        readonly editingGeometryFeatureId: string = null,
        readonly showGround: boolean = true,
        readonly associations: AssociatedFeature[] = [],
        readonly associationsFlat: AssociationJson[] = [],
        readonly selectedAssociation: AssociatedFeature = null,
        readonly targetAssociationTypes: DisplayFeatureType[] = [],
        // XXX NVDB-6145 targetFeatures is in reality either CommonAssociationFeature or DisplayFeature, see usage of
        // ASSOCIATIONS_RECEIVE_POTENTIAL_CHILDREN. It doesn't show any errors because all types are as usual lost
        // in the reducer. Using an union type for this breaks down somewhere in the components when accessing associations,
        // which are only available in CommonAssociationFeature
        readonly targetFeatures: CommonAssociationFeature[] = [],
        readonly selectedTargetFeatures: CommonAssociationFeature[] = [],
        readonly selectedAssociationTargetType: FeatureTypeAttributes = null,
        readonly selectedSourceFeature: DisplayFeature = null,
        readonly tab: AssociationScope = null,
        readonly attributePopoverData: Definition[] = [],
        readonly autoAssociateCount: number = -1,
        readonly nvdbFeaturetypes: FeatureTypeSummary[] = [],
        readonly nvdbSearchDistance: number = 10
    ) { }

    private create(params: Partial<AssociationsState>) {
        const state = new AssociationsState(
            params.datacatalog,
            params.contractId,
            params.isFetching,
            params.featureTypes,
            params.selectedFeatureTypeId,
            params.selectedFeatureType,
            params.additionalSelectedFeatureTypes,
            params.features,
            params.selectedFeatures,
            params.user,
            params.isShowingExistingFeatures,
            params.existingFeatures,
            params.deletions,
            params.showingFullscreen,
            params.splitScreenDirection,
            params.editingGeometryFeatureId,
            params.showGround,
            params.associations,
            params.associationsFlat,
            params.selectedAssociation,
            params.targetAssociationTypes,
            params.targetFeatures,
            params.selectedTargetFeatures,
            params.selectedAssociationTargetType,
            params.selectedSourceFeature,
            params.tab,
            params.attributePopoverData,
            params.autoAssociateCount,
            params.nvdbFeaturetypes,
            params.nvdbSearchDistance
        );
        return state;
    }

    update = ({
                  datacatalog = this.datacatalog,
                  contractId = this.contractId,
                  isFetching = this.isFetching,
                  featureTypes = this.featureTypes,
                  selectedFeatureTypeId = this.selectedFeatureTypeId,
                  selectedFeatureType = this.selectedFeatureType,
                  additionalSelectedFeatureTypes = this.additionalSelectedFeatureTypes,
                  features = this.features,
                  selectedFeatures = this.selectedFeatures,
                  user = this.user,
                  isShowingExistingFeatures = this.isShowingExistingFeatures,
                  existingFeatures = this.existingFeatures,
                  deletions = this.deletions,
                  showingFullscreen = this.showingFullscreen,
                  splitScreenDirection = this.splitScreenDirection,
                  editingGeometryFeatureId = this.editingGeometryFeatureId,
                  showGround = this.showGround,
                  associations = this.associations,
                  associationsFlat = this.associationsFlat,
                  selectedAssociation = this.selectedAssociation,
                  targetAssociationTypes = this.targetAssociationTypes,
                  targetFeatures: targetFeatures = this.targetFeatures,
                  selectedTargetFeatures = this.selectedTargetFeatures,
                  selectedAssociationTargetType = this.selectedAssociationTargetType,
                  selectedSourceFeature = this.selectedSourceFeature,
                  tab = this.tab,
                  attributePopoverData = this.attributePopoverData,
                  autoAssociateCount = this.autoAssociateCount,
                  nvdbFeaturetypes = this.nvdbFeaturetypes,
                  nvdbSearchDistance = this.nvdbSearchDistance
              } = {}) => ({
        datacatalog,
        contractId,
        isFetching,
        featureTypes,
        selectedFeatureTypeId,
        selectedFeatureType,
        additionalSelectedFeatureTypes,
        features,
        selectedFeatures,
        user,
        isShowingExistingFeatures,
        existingFeatures,
        deletions,
        showingFullscreen,
        splitScreenDirection,
        editingGeometryFeatureId,
        showGround,
        associations,
        associationsFlat,
        selectedAssociation,
        targetAssociationTypes,
        targetFeatures,
        selectedTargetFeatures,
        selectedAssociationTargetType,
        selectedSourceFeature,
        tab,
        attributePopoverData,
        autoAssociateCount,
        nvdbFeaturetypes,
        nvdbSearchDistance
        });

    public withNvdbFeatureTypes(nvdbFeaturetypes: FeatureTypeSummary[]): AssociationsState {
        const params = this.update({nvdbFeaturetypes});
        return this.create(params);
    }

    public withNvdbSearchDistance(nvdbSearchDistance: number): AssociationsState {
        const params = this.update({nvdbSearchDistance});
        return this.create(params);
    }

    public withAttributePopoverData(attributePopoverData: Definition[]): AssociationsState {
        const params = this.update({attributePopoverData});
        return this.create(params);
    }

    public withSelectedSourceFeature(selectedSourceFeature: DisplayFeature): AssociationsState {
        const params = this.update({selectedSourceFeature});
        return this.create(params);
    }

    public withUnselectAssociation(): AssociationsState {
        const params = this.update({
            selectedSourceFeature: null,
            selectedAssociation: null,
            targetAssociationTypes: [],
            targetFeatures: [],
            selectedTargetFeatures: [],
            selectedAssociationTargetType: null
        });
        return this.create(params);
    }


    public withSelectedFeatureType(selectedFeatureType: DisplayFeatureType): AssociationsState {
        const params = this.update({selectedFeatureType});
        return this.create(params);
    }

    public withSelectedAssociationTargetType(selectedAssociationTargetType: FeatureTypeAttributes): AssociationsState {
        const params = this.update({selectedAssociationTargetType});
        return this.create(params);
    }

    public withSelectedTargetFeatures(selectedTargetFeatures: CommonAssociationFeature[]): AssociationsState {
        const params = this.update({selectedTargetFeatures});
        return this.create(params);
    }


    public withTargetAssociationTypes(targetAssociationTypes: DisplayFeatureType[]): AssociationsState {
        const params = this.update({targetAssociationTypes});
        return this.create(params);
    }

    public withTargetFeatures(targetFeatures: CommonAssociationFeature[]): AssociationsState {
        const params = this.update({targetFeatures});
        return this.create(params);
    }


    public withContractId(contractId: string): AssociationsState {
        const params = this.update({
            contractId,
            selectedFeatureTypeId: null,
            selectedFeatureType: null,
            selectedAssociation: null,
            targetAssociationTypes: [],
            targetFeatures: [],
            selectedTargetFeatures: [],
            selectedAssociationTargetType: null,
            selectedSourceFeature: null,
            tab: null
        });
        return this.create(params);
    }

    public withFeatureTypes(featureTypes: FeatureTypeSummary[]): AssociationsState {
        const params = this.update({featureTypes});
        return this.create(params);
    }

    public withFeatures(features: DisplayFeature[]): AssociationsState {
        return this.create(this.update({features}));
    }

    public withIsFetching(isFetching: boolean): AssociationsState {
        const params = this.update({isFetching});
        return this.create(params);
    }

    public withSelectedFeatureTypeId(selectedFeatureTypeId: number): AssociationsState {
        const params = this.update({selectedFeatureTypeId});
        return this.create(params);
    }

    public withUser(user: UserSession): AssociationsState {
        const params = this.update({user});
        return this.create(params);
    }

    public withSelectedFeatures(selectedFeatures: string[]): AssociationsState {
        const params = this.update({selectedFeatures});
        return this.create(params);
    }

    public withIsShowingExistingFeatures(isShowingExistingFeatures: boolean): AssociationsState {
        const params = this.update({isShowingExistingFeatures});
        return this.create(params);
    }

    public withExistingFeatures(existingFeatures: NvdbFeature[]): AssociationsState {
        const params = this.update({existingFeatures});
        return this.create(params);
    }

    public withAdditionalSelectedFeatureTypes(additionalSelectedFeatureTypes: DisplayFeatureType[]): AssociationsState {
        const params = this.update({additionalSelectedFeatureTypes});
        return this.create(params);
    }

    public withNoSelectedFeatureType(): AssociationsState {
        const params = this.update(
            {
                selectedFeatureTypeId: null,
                selectedFeatureType: null,
                additionalSelectedFeatureTypes: [],
                selectedFeatures: null,
                isShowingExistingFeatures: false,
                existingFeatures: []
            }
        );
        return this.create(params);
    }

    public withAssociations(associations: AssociatedFeature[]): AssociationsState {
        const params = this.update({associations});
        return this.create(params);
    }

    public withAssociationsFlat(associationsFlat: AssociationJson[]): AssociationsState {
        const params = this.update({associationsFlat});
        return this.create(params);
    }

    public withSelectedAssociation(selectedAssociation: AssociatedFeature): AssociationsState {
        const params = this.update({selectedAssociation});
        return this.create(params);
    }

    public withTab(tab: AssociationScope): AssociationsState {
        const params = this.update({tab});
        return this.create(params);
    }

    public withAutoAssociationFeedback(autoAssociateCount: number): AssociationsState {
        const params = this.update({autoAssociateCount});
        return this.create(params);
    }

    public withDatacatalog(datacatalog: datacatalog): AssociationsState {
        return this.create(this.update({datacatalog}));
    }

}